<template>
  <li class="w-full">
    <div
      v-if="loading"
      class="w-full h-9 rounded-md animate-pulse bg-surface-300"
    ></div>

    <NuxtLink
      v-if="!loading && linkTo"
      :to="linkTo"
      class="grid items-center gap-3 pl-[2px] py-[2px] rounded-[8px] group border duration-300"
      :class="{
        'grid-cols-[2rem_auto_auto]': $slots.after,
        'grid-cols-[2rem_auto_2rem]': !$slots.after,
        'cursor-pointer': true,
        'hover:bg-primary-100 text-primary-600': !isActive,
        'text-surface-700 font-medium border-transparent': !isActive,
        'bg-primary-400 text-white font-medium border-primary-400 border-1':
          isActive,
      }"
    >
      <div class="w-full flex justify-center">
        <AppIconSquare
          :icon="icon"
          :is-active="isActive"
          class="text-base px-2"
          :class="{
            'text-surface-500': !isActive,
            'group-hover:text-surface-700 duration-300': !isActive,
          }"
        />
      </div>
      <span class="text-sm truncate">{{ label }}</span>
      <slot name="after"></slot>
    </NuxtLink>

    <div
      v-if="!loading && !linkTo"
      class="grid items-center gap-3 pl-[2px] py-[2px] rounded-[8px] group border duration-300"
      :class="{
        'grid-cols-[2rem_auto_auto]': $slots.after,
        'grid-cols-[2rem_auto_2rem]': !$slots.after,
        'text-surface-700 font-medium border-transparent': true,
      }"
    >
      <div class="w-full flex justify-center">
        <AppIconSquare
          :icon="icon"
          class="text-base px-2 text-surface-500"
        />
      </div>
      <span class="text-sm truncate">{{ label }}</span>
      <slot name="after"></slot>
    </div>
  </li>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

const route = useRoute()

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  icon: {
    type: String,
    required: true,
  },
  to: {
    type: [String, Object] as PropType<string | RouteLocationRaw>,
    required: false,
    default: undefined,
  },
  toName: {
    type: String,
    required: false,
    default: undefined,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const linkTo = computed(() => {
  if (props.to) return props.to
  if (props.toName) return { name: props.toName }
  return undefined
})

function isNamedRouteActive(targetName: string): boolean {
  const current = route.name?.toString()
  if (!current) return false
  // Workspace root — exact match only so nested routes stay inactive on Dashboard.
  if (targetName === 'organizationSlug-workspaceId')
    return current === targetName
  return current === targetName || current.startsWith(`${targetName}-`)
}

const isActive = computed(() => {
  if (typeof props.to === 'string') {
    const path = route.path
    // Workspace root (`/:organizationSlug/:workspaceId`) — exact match only.
    if (/^\/[^/]+\/[^/]+$/.test(props.to)) return path === props.to
    return path === props.to || path.startsWith(`${props.to}/`)
  }

  if (
    props.to &&
    typeof props.to === 'object' &&
    'name' in props.to &&
    props.to.name
  ) {
    return isNamedRouteActive(props.to.name.toString())
  }

  if (!props.toName) return false
  return isNamedRouteActive(props.toName)
})
</script>
